import { useQuery } from '@apollo/client';
import { gql } from '__generated__';
import { FC } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import SidebarLayout from 'layouts/SidebarLayout';

import ErrorMessage from 'components/ErrorMessage';
import HorizontalTabs from 'components/HorizontalTabs';
import LoadingIndicator from 'components/LoadingIndicator';
import Navbar from 'components/Navbar';

import { getUserRole } from 'utils/auth';

const DEPARTMENT_QUERY = gql(`
  query Department($departmentId: ID!) {
    department(id: $departmentId) {
      id
      name
    }
  }
`);

const DepartmentPage: FC = () => {
  const navigate = useNavigate();
  const { departmentId } = useParams<{ departmentId: string }>();

  const { loading, error, data, refetch } = useQuery(DEPARTMENT_QUERY, {
    variables: {
      departmentId: departmentId as string,
    },
  });

  const renderContent = () => {
    if (loading) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const department = data.department;

    const userRole = getUserRole();

    const tabs = [
      {
        key: 'information',
        label: 'Information',
        route: `/departments/${departmentId}`,
        exact: true,
      },
      {
        key: 'users',
        label: 'Users',
        route: `/departments/${departmentId}/users`,
      },
      {
        key: 'divisions',
        label: 'Divisions',
        route: `/departments/${departmentId}/divisions`,
      },
      {
        key: 'designations',
        label: 'Designations',
        route: `/departments/${departmentId}/designations`,
      },
      ...(userRole === 'SUPER_ADMIN' || userRole === 'ADMIN'
        ? [
            {
              key: 'identity-cards',
              label: 'Identity Cards',
              route: `/departments/${departmentId}/identity-cards`,
            },
            { key: 'admins', label: 'Admins', route: `/departments/${departmentId}/admins` },
            {
              key: 'print-sessions',
              label: 'Print Sessions',
              route: `/departments/${departmentId}/print-sessions`,
            },
          ]
        : []),
    ];

    return (
      <>
        <Navbar
          title={`${department.name}`}
          subTitle="Department"
          onBackButtonClick={() => navigate(-1)}
        />
        <HorizontalTabs tabs={tabs} />
      </>
    );
  };

  return (
    <SidebarLayout>
      {renderContent()}
      <Outlet />
    </SidebarLayout>
  );
};

export default DepartmentPage;
